import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Input,
  SVGIcon,
} from '@pearsontechnology/aero-components';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

import { selectWorkspacesList } from 'Workspace/Admin/store/selectors';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import enhanceWithServices from 'hocs/enhanceWithServices';
import Modal, { ModalContent, ModalActions } from 'components/Modal';

import {
  BackButton,
  IconBack,
  Label,
  List,
  ListItem,
} from './styles';
import messages from './messages';
import DuplicateWarningModal from 'Workspace/Projects/components/DuplicateWarningModal';

function DuplicateCoursewareModal({
  onDismiss,
  visible,
  workspaces,
  handleDuplicateCourseware,
  projectService,
  initialSelectedWorkspace,
  initialProjectsList = undefined,
  initialFilterState = '',
  activityService,
  selectedActivityId,
}) {
  const [filterValue, setFilterValue] = useState(initialFilterState);
  const handleInputChange = (e) => {
    setFilterValue(e.target.value);
  };
  const filterList = (list, value) => {
    if (!value) {
      return list;
    }
    const searchValue = value.toLowerCase();
    return list?.filter((item) => item.name.toLowerCase().indexOf(searchValue) >= 0);
  };

  const [selectedWorkspace, setSelectedWorkspace] = useState(initialSelectedWorkspace);
  const [touchedWorkspace, setToucheWorkspace] = useState(initialSelectedWorkspace);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [isDuplicateTargetLocationSame, setIsDuplicateTargetLocationSame] = useState(false);
  const onDismissWarning = () => setShowDuplicateModal(false);
  const { projectId: currentProjectId } = useParams();

  const handleWorkspaceClick = (workspace) => {
    setSelectedWorkspace(workspace);
    setToucheWorkspace(workspace);
    setFilterValue('');
  };
  const handleBackClick = () => {
    setSelectedWorkspace(undefined);
    setToucheWorkspace(undefined);
    setSelectedProject(undefined);
    setFilterValue('');
  };

  const isObjectAndNotEmpty = (workspace) => workspace && workspace.constructor === Object && Object.keys(workspace).length !== 0;

  const [projectList, setProjectList] = useState(initialProjectsList);
  const [selectedProject, setSelectedProject] = useState();

  const sortCoursewareByField = (field) => (a, b) => {
    const getValue = (obj, path) => path.split('.').reduce((o, k) => o?.[k], obj);
    const aValue = getValue(a, field)?.toLowerCase?.();
    const bValue = getValue(b, field)?.toLowerCase?.();
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
    return 0;
  };

  useEffect(() => {
    (async () => {
      if (isObjectAndNotEmpty(selectedWorkspace)) {
        const projectsList = await projectService.getProjectsList(selectedWorkspace.id);
        projectsList.sort(sortCoursewareByField('name'));
        setProjectList(projectsList);
      }
    })();
  }, [selectedWorkspace, projectService]);

  useEffect(() => {
    (async () => {
      if (
        (isObjectAndNotEmpty(initialSelectedWorkspace) &&
          isObjectAndNotEmpty(selectedWorkspace) &&
          initialSelectedWorkspace.id !== selectedWorkspace.id) ||
        (isObjectAndNotEmpty(initialSelectedWorkspace) &&
          isObjectAndNotEmpty(selectedWorkspace) === false)
      ) {
        const projectsList = await projectService.getProjectsList(initialSelectedWorkspace.id);
        projectsList.sort(sortCoursewareByField('name'));
        setProjectList(projectsList);
      }
    })();
  }, [initialSelectedWorkspace]);

  const handleDismiss = () => {
    onDismiss();
    onDismissWarning();
    setSelectedWorkspace(initialSelectedWorkspace);
    setSelectedProject(undefined);
    setFilterValue('');
  };

  const handleDuplicateClick = () => {
    if (selectedProject?.id === currentProjectId) {
      setIsDuplicateTargetLocationSame(true);
    } else {
      setIsDuplicateTargetLocationSame(false);
    }
    handleDismiss();
    setShowDuplicateModal(true);
    setSelectedProject(selectedProject);
  };

  const handleDuplicateProcess = () => {
    handleDuplicateCourseware(selectedProject?.id);
    handleDismiss();
  };

  return (
    <>
      <Modal
        visible={visible}
        onDismiss={handleDismiss}
        title={intl.formatMessage(!selectedWorkspace ? messages.modalTitle1 : messages.modalTitle2)}
      >
        <ModalContent unpadBottom>
          <Input.Search
            value={filterValue}
            width={'100%'}
            onChange={handleInputChange}
            placeholder={intl.formatMessage(messages.searchTextPlaceholder)}
            label={intl.formatMessage(messages.searchTextPlaceholder)}
          />
        </ModalContent>
        <List>
          <BackButton
            disabled={!selectedWorkspace}
            onClick={handleBackClick}
          >
            <IconBack
              icon="back"
              disabled={!selectedWorkspace}
              size="small"
            />
            <Label>
              {intl.formatMessage(messages.backButton)}
            </Label>
          </BackButton>
          {!selectedWorkspace
            ? filterList(workspaces, filterValue)?.map((workspace) => (
              <ListItem
                key={workspace.id}
                onClick={() => handleWorkspaceClick(workspace)}
              >
                <SVGIcon
                  icon="duotoneProjects"
                />
                {workspace.name}
              </ListItem>
            ))
            : filterList(projectList, filterValue)?.map((project) => (
              <ListItem
                key={project.id}
                onClick={() => setSelectedProject(project)}
                selected={selectedProject?.id === project.id}
              >
                <SVGIcon
                  icon="duotoneProjects"
                />
                {project.name}
              </ListItem>
            ))
          }
        </List>

        <ModalActions scrim>
          <Button grey onClick={handleDismiss}>Cancel</Button>
          <Button
            primary
            onClick={handleDuplicateClick}
            disabled={selectedProject === undefined}
            id="submit"
          >
            {intl.formatMessage(messages.duplicateButtonText)}
          </Button>
        </ModalActions>
      </Modal>
      <DuplicateWarningModal
        showDuplicateModal={showDuplicateModal}
        isDuplicateTargetLocationSame={isDuplicateTargetLocationSame}
        onDismiss={handleDismiss}
        handleDuplicateProcess={handleDuplicateProcess}
        activityService={activityService}
        selectedActivityId={selectedActivityId}
        selectedWorkspace={touchedWorkspace}
      />
    </>
  );
}

DuplicateCoursewareModal.propTypes = {
  onDismiss: PropTypes.func,
  visible: PropTypes.bool,
  workspaces: PropTypes.array,
  handleDuplicateCourseware: PropTypes.func,
  projectService: PropTypes.object,
  initialSelectedWorkspace: PropTypes.object,
  initialProjectsList: PropTypes.array,
  initialFilterState: PropTypes.string,
  activityService: PropTypes.object,
  selectedActivityId: PropTypes.string,
};

const mapStateToProps = (state) => ({
  workspaces: selectWorkspacesList(state),
});

const withConnect = connect(mapStateToProps);

export default compose(
  withConnect,
  enhanceWithServices,
)(DuplicateCoursewareModal);
